import React, { createContext, useContext, useState, useMemo } from 'react';

const LIGHT = {
  bg: '#F8F9FA',
  surface: '#FFFFFF',
  surfaceAlt: '#FAFAFA',
  border: '#E8EAED',
  borderLight: '#F0F0F0',
  borderSubtle: '#F4F5F6',
  hover: '#FAFAFA',
  textPrimary: '#111111',
  textSecondary: '#666666',
  textTertiary: '#444444',
  labelMuted: '#AAAAAA',
  labelLight: '#BBBBBB',
  labelLighter: '#CCCCCC',
  labelClose: '#CCC',
  inputBg: '#FFFFFF',
  pillBg: '#FFFFFF',
  pillInactiveBg: '#F4F5F6',
  badgeBg: '#F4F5F6',
  divider: '#E8EAED',
  shadow: 'rgba(0,0,0,0.04)',
  shadowHover: 'rgba(0,0,0,0.08)',
  panelShadow: 'rgba(0,0,0,0.06)',
  selectedRowBg: '#F8FFF9',
  // Accent colors
  green: '#0D7A4E',
  greenBg: '#E8F8F2',
  greenBorder: '#A8DEC8',
  gold: '#8A6A00',
  goldBg: '#FFF8E0',
  goldBorder: '#E0CC70',
  red: '#B8001A',
  redBg: '#FFF0F2',
  redBorder: '#F0A0A8',
  // Score bar track
  scoreTrack: '#F0F0F0',
  // Trending bar
  trendingBg: 'linear-gradient(90deg, #FFFDF5 0%, #FFF9EC 50%, #FFFDF5 100%)',
  trendingBorder: '#F0E8D0',
  trendingFadeLeft: 'linear-gradient(90deg, #FFFDF5 60%, transparent 100%)',
  trendingFadeRight: 'linear-gradient(270deg, #FFFDF5 30%, transparent 100%)',
  // Banners
  bannerGradient: 'linear-gradient(135deg, #E8F8F2 0%, #FFF8E0 100%)',
  bestPicksBanner: 'linear-gradient(135deg, #E8F8F2 0%, #FFF8E0 50%, #EEF1FD 100%)',
  trendingBanner: 'linear-gradient(135deg, #FFF8E0 0%, #FFFDF5 40%, #E8F8F2 100%)',
  // Active pill (sort, stables only, etc)
  activeBg: '#111111',
  activeText: '#FFFFFF',
  // VS text
  vsText: '#CCC',
  // Winner/loser in compare
  loserText: '#999',
};

const DARK = {
  bg: '#0D1117',
  surface: '#161B22',
  surfaceAlt: '#1C2128',
  border: '#30363D',
  borderLight: '#30363D',
  borderSubtle: '#21262D',
  hover: '#1C2128',
  textPrimary: '#E6EDF3',
  textSecondary: '#8B949E',
  textTertiary: '#C9D1D9',
  labelMuted: '#6E7681',
  labelLight: '#484F58',
  labelLighter: '#484F58',
  labelClose: '#484F58',
  inputBg: '#0D1117',
  pillBg: '#161B22',
  pillInactiveBg: '#21262D',
  badgeBg: '#21262D',
  divider: '#30363D',
  shadow: 'rgba(0,0,0,0.3)',
  shadowHover: 'rgba(0,0,0,0.5)',
  panelShadow: 'rgba(0,0,0,0.4)',
  selectedRowBg: '#12261A',
  // Accent colors — brighter for dark bg
  green: '#2EA043',
  greenBg: '#12261A',
  greenBorder: '#1B4D2E',
  gold: '#D4A72C',
  goldBg: '#2A2212',
  goldBorder: '#4D3D1A',
  red: '#F85149',
  redBg: '#2D1115',
  redBorder: '#5C2028',
  // Score bar track
  scoreTrack: '#30363D',
  // Trending bar
  trendingBg: 'linear-gradient(90deg, #161B22 0%, #1C2128 50%, #161B22 100%)',
  trendingBorder: '#30363D',
  trendingFadeLeft: 'linear-gradient(90deg, #161B22 60%, transparent 100%)',
  trendingFadeRight: 'linear-gradient(270deg, #161B22 30%, transparent 100%)',
  // Banners
  bannerGradient: 'linear-gradient(135deg, #12261A 0%, #2A2212 100%)',
  bestPicksBanner: 'linear-gradient(135deg, #12261A 0%, #2A2212 50%, #161B30 100%)',
  trendingBanner: 'linear-gradient(135deg, #2A2212 0%, #1C2128 40%, #12261A 100%)',
  // Active pill
  activeBg: '#E6EDF3',
  activeText: '#0D1117',
  // VS text
  vsText: '#484F58',
  // Winner/loser in compare
  loserText: '#484F58',
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(false);

  const value = useMemo(() => ({
    dark,
    colors: dark ? DARK : LIGHT,
    toggle: () => setDark(d => !d),
  }), [dark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
